// Strict E.164: leading +, then 1-15 digits (first digit can't be 0).
// Mirrors the DB CHECK constraint on chat_sessions.visitor_phone and
// the server-side regex in the agent's bootstrap controller — if it
// passes here it will pass there.
const E164 = /^\+[1-9][0-9]{1,14}$/;

export function isE164(phone: string): boolean {
  return E164.test(phone);
}

// Coerce user input into E.164. Accepts organic formats like
// "(888) 450-2020", "888-450-2020", "888.450.2020", "1 888 450 2020",
// or already-canonical "+14165550100". Defaults to +1 when the user
// types a North-American-shaped number without a country code — the
// most common form for our target market. International numbers must
// be typed with the leading + (we can't safely guess the country
// otherwise).
//
//   "(888) 450-2020"         → "+18884502020"   (10 digits → +1 default)
//   "1-888-450-2020"         → "+18884502020"   (11 digits, starts with 1)
//   "+44 20 7946 0958"       → "+442079460958"  (international, + preserved)
//   "888"                    → "888"            (too short — strict validator
//                                                will reject and surface error)
export function normalizePhone(input: string): string {
  if (!input) return "";
  const trimmed = input.trim();
  if (!trimmed) return "";
  const hasPlus = trimmed.startsWith("+");
  const digitsOnly = trimmed.replace(/\D/g, "");
  if (!digitsOnly) return "";

  if (hasPlus) {
    return "+" + digitsOnly;
  }
  // 11 digits starting with 1: full North American number with
  // country code, just needs the +.
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return "+" + digitsOnly;
  }
  // 10 digits: North American number missing the country code. Prepend +1.
  if (digitsOnly.length === 10) {
    return "+1" + digitsOnly;
  }
  // Neither matches — return the raw digits without +. The strict
  // validator will reject and the form surfaces a friendly error.
  return digitsOnly;
}
